import React, { Fragment, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import VehicleAuditViewIssueListItem from "../../../components/lists/listItems/VehicleAuditViewIssueListItem/VehicleAuditViewListItem";
import VehicleAuditViewListItem from "../../../components/lists/listItems/VehicleAuditViewListItem/VehicleAuditViewListItem";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import TabbedView from "../../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../../components/lists/TabbedView/TabbedViewPage";
import { ProgressView } from "../../../components/notifications/progress/ProgressView";

import { api, IVehicleManagerGetAuditIssues, IVehicleManagerGetAuditItems, VehicleAuditIssue, VehicleAuditItem, VehicleAuditItemStatus } from "../../../logic/api";

import { SessionContext } from "../../../utility/useSession";
import { useDate } from "../../../utility/useDate";

import DashPage from "../../dash/DashPage";
import NotFoundPage from "../../error/NotFoundPage";
import { useTranslation } from "react-i18next";

interface IVehicleAuditViewParams {
    auditId: string;
}

const VehicleAuditView = () => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();

    if (session.current.site == null) {
        return <Fragment></Fragment>;
    }

    const laneAuditEnabled = session.current.site.preferences.lane_audit_enabled == true;

    if (laneAuditEnabled == false) {
        return <NotFoundPage title={t("notFoundPage.notAvailable")} message={t("notFoundPage.featureNotEnabled")} />
    }
    const params: IVehicleAuditViewParams = useParams();
    const { toFormattedDateTimeString } = useDate();
    const auditId = parseInt(params.auditId);

    const [paramsScanned, setParamsScanned] = useState<IVehicleManagerGetAuditItems | null>(null);
    const [paramsMissing, setParamsMissing] = useState<IVehicleManagerGetAuditItems | null>(null);
    const [paramsUnexpected, setParamsUnexpected] = useState<IVehicleManagerGetAuditItems | null>(null);
    const [paramsIssues, setParamsIssues] = useState<IVehicleManagerGetAuditIssues | null>(null);

    const fetchAudit = api.hooks.vehicleManager.getAudit();
    useEffect(() => {
        if (!isNaN(auditId)) {
            fetchAudit.send(session.current.context, {
                auditId: auditId
            });
        }
    }, [auditId]);

    useEffect(() => {
        if (fetchAudit.data != null && fetchAudit.data.id != null) {
            setParamsScanned({
                auditId: fetchAudit.data.id,
                status: VehicleAuditItemStatus.expected
            });
            setParamsMissing({
                auditId: fetchAudit.data.id,
                status: VehicleAuditItemStatus.missing
            });
            setParamsUnexpected({
                auditId: fetchAudit.data.id,
                status: VehicleAuditItemStatus.unexpected
            });
            setParamsIssues({
                auditId: fetchAudit.data.id,
            });
        } else {
            setParamsScanned(null);
        }
    }, [fetchAudit.data]);

    return <DashPage>
        <ProgressView status={fetchAudit.status}>

            {fetchAudit.data != null && (
                <Fragment>
                    <div style={{ padding: 20, textAlign: "center" }}>
                        <div>
                            <h1>{fetchAudit.data.location.parentName}, {fetchAudit.data.location.name}</h1>
                            <h2>{toFormattedDateTimeString(new Date(fetchAudit.data.createDate))} - {fetchAudit.data.createdBy.firstName} {fetchAudit.data.createdBy.lastName}</h2>
                        </div>

                        <TabbedView>
                            <TabbedViewPage title={t("keys.missingCount", { count: fetchAudit.data.missingCount })}>
                                <div style={{ marginBottom: 20 }}>
                                    <i>{t("vehicles.vehicleNotScannedDuringAudit", { location: fetchAudit.data.location.name })}</i>
                                </div>
                                {paramsMissing != null && (
                                    <div style={{ width: "100%", maxWidth: 600, display: "inline-block" }}>
                                        <PagedDataView<IVehicleManagerGetAuditItems, VehicleAuditItem>
                                            apiHook={api.hooks.vehicleManager.getAuditItems}
                                            parameters={paramsMissing}
                                            renderItem={(item) => {
                                                return <VehicleAuditViewListItem item={item} />
                                            }}
                                        ><div style={{ backgroundColor: "#eee", padding: 10 }}>
                                                <b>{t("vehicles.noMissingVehicles")}</b>
                                            </div>
                                        </PagedDataView>
                                    </div>
                                )}
                            </TabbedViewPage>

                            <TabbedViewPage title={t("keys.unexpectedCount", { count: fetchAudit.data.unexpectedCount })}>
                                <div style={{ marginBottom: 20 }}>
                                    <i>{t("vehicles.vehiclesFoundDuringAudit", { location: fetchAudit.data.location.name })}</i>
                                </div>
                                {paramsUnexpected != null && (
                                    <div style={{ width: "100%", maxWidth: 600, display: "inline-block" }}>
                                        <PagedDataView<IVehicleManagerGetAuditItems, VehicleAuditItem>
                                            apiHook={api.hooks.vehicleManager.getAuditItems}
                                            parameters={paramsUnexpected}
                                            renderItem={(item) => {
                                                return <VehicleAuditViewListItem item={item}></VehicleAuditViewListItem>
                                            }}
                                        ><div style={{ backgroundColor: "#eee", padding: 10 }}>
                                                <b>{t("vehicles.noUnexpectedVehicles")}</b>
                                            </div>
                                        </PagedDataView>
                                    </div>
                                )}
                            </TabbedViewPage>
                            <TabbedViewPage title={t("vehicles.foundCount", { count: fetchAudit.data.scanCount - fetchAudit.data.unexpectedCount })}>
                                <div style={{ marginBottom: 20 }}>
                                    <i>{t("vehicles.vehcilesFoundExcUnexpectedVehicles")}</i>
                                </div>
                                {paramsScanned != null && (
                                    <div style={{ width: "100%", maxWidth: 600, display: "inline-block" }}>
                                        <PagedDataView<IVehicleManagerGetAuditItems, VehicleAuditItem>
                                            apiHook={api.hooks.vehicleManager.getAuditItems}
                                            parameters={paramsScanned}
                                            renderItem={(item) => {
                                                return <VehicleAuditViewListItem item={item}></VehicleAuditViewListItem>
                                            }}
                                        ><div style={{ backgroundColor: "#eee", padding: 10 }}>
                                                <b>{t("vehicles.noExpectedVehicles")}</b>
                                            </div>
                                        </PagedDataView>
                                    </div>
                                )}
                            </TabbedViewPage>
                            <TabbedViewPage title={t("vehicles.issuesCount", { count: fetchAudit.data.issueCount })}>
                                <div style={{ marginBottom: 20 }}>
                                    <i>{t("vehicles.issuesLoggedDuringAudit")}</i>
                                </div>
                                {paramsIssues != null && (
                                    <div style={{ width: "100%", maxWidth: 600, display: "inline-block" }}>
                                        <PagedDataView<IVehicleManagerGetAuditIssues, VehicleAuditIssue>
                                            apiHook={api.hooks.vehicleManager.getAuditIssues}
                                            parameters={paramsIssues}
                                            renderItem={(item) => {
                                                return <VehicleAuditViewIssueListItem item={item}></VehicleAuditViewIssueListItem>
                                            }}
                                        ><div style={{ backgroundColor: "#eee", padding: 10 }}>
                                                <b>{t("vehicles.noIssuesFound")}</b>
                                            </div>
                                        </PagedDataView>

                                    </div>
                                )}
                            </TabbedViewPage>
                        </TabbedView>

                    </div>
                </Fragment>
            )}
        </ProgressView>
    </DashPage>
}

export default VehicleAuditView;
